import { forEach, sortBy } from 'lodash';
import createReReSelector from 're-reselect';

import { InterestsIdsType } from '../../components/InterestsSetEditor/InterestsSetEditor';
import { mapCurrentAudienceTargetings } from '../../mappers/audienceTargetings/audienceTargetings';
import { mapAudienceTargetingsToRetargetingConditionsApiFormat } from '../../mappers/retargetingCondition/retargetingCondition';
import { defaultAudienceTargetingsEditorsState } from '../../reducers/targetings/audience';
import {
    audienceTargetingsTypeOrderedList,
    getAudienceTargetingsEditorDescription,
    getPredictorInterestsText,
    getPredictorMetricsText,
    getPredictorPreviewText,
    getPreviewMetrikaSegment,
    getPreviewSegment,
    transformInterestsToSegments
} from '../../service/CryptaHelpers/CryptaHelpers';
import {
    AudienceTargetingsEditorDescription,
    AudienceTargetingsEditorState,
    AudienceTargetingsType
} from '../../typings/audienceTargetings';
import { SegmentCategoryText } from '../../typings/crypta-segment-selector';
import { CryptaSegmentsByType } from '../../typings/cryptaSegments';
import { GdUpdateCpmRetargetingConditionItemInput, WebGoalType } from '../../typings/graphql-grid';
import { EditCpmGroupDataValue } from '../../typings/groupsEditState';
import { TextPredictor } from '../../typings/predictor';
import { RetargetingConditionAudienceEditorState, RetargetingConditionData } from '../../typings/retargeting';
import { AudienceTargetingsEditorsState, State } from '../../typings/state';
import {
    getAllCryptaSegmentsByType,
    getAllSegmentsGroupByType,
    getInterestsIds
} from '../cryptaSegments/cryptaSegments';
import { selectEditGroupValueInfo } from '../groupEdit/group/group';

export const getCurrentAudienceTargetingsServerValue = createReReSelector(
    (state: State, groupId: string) => selectEditGroupValueInfo(state, groupId),
    (state: State, groupId: string) => ({
        interestsTargetingEditor: state.interestsTargetingEditor[groupId],
        cryptaSocialDemoEditor: state.cryptaSocialDemoEditor[groupId],
        cryptaGroupFamilyEditor: state.cryptaGroupFamilyEditor[groupId],
        cryptaAudioGenresEditor: state.cryptaAudioGenresEditor[groupId],
        cryptaBehavioralEditor: state.cryptaBehavioralEditor[groupId],
        retargetingAudienceEditor: state.retargetingAudienceEditor[groupId]
    }),

    (state: State) => getAllCryptaSegmentsByType(state),
    (
        groupValue: EditCpmGroupDataValue,
        audienceTargetingsEditors,
        cryptaSegmentsByType: CryptaSegmentsByType
    ): GdUpdateCpmRetargetingConditionItemInput => {
        return mapAudienceTargetingsToRetargetingConditionsApiFormat({
            audienceTargetings: mapCurrentAudienceTargetings(audienceTargetingsEditors, groupValue.audienceTargetings),
            audienceTargetingsTemplateName: groupValue.audienceTargetingsTemplateName,
            cryptaSegmentsByType: cryptaSegmentsByType
        });
    }
)((state, groupId) => groupId);

export const getCurrentAudienceTargetingsText = createReReSelector(
    (state: State, groupId: string) => selectEditGroupValueInfo(state, groupId),
    (state: State, groupId: string) => ({
        interestsTargetingEditor: state.interestsTargetingEditor[groupId],
        cryptaSocialDemoEditor: state.cryptaSocialDemoEditor[groupId],
        cryptaGroupFamilyEditor: state.cryptaGroupFamilyEditor[groupId],
        cryptaAudioGenresEditor: state.cryptaAudioGenresEditor[groupId],
        cryptaBehavioralEditor: state.cryptaBehavioralEditor[groupId]
    }),
    (state: State) => getInterestsIds(state),
    (state: State) => getAllSegmentsGroupByType(state),
    (
        groupValue: EditCpmGroupDataValue,
        audienceTargetingsEditors,
        interestId: InterestsIdsType,
        cryptaSegmentsByType: CryptaSegmentsByType
    ): TextPredictor[] => {
        const audienceTargetings = mapCurrentAudienceTargetings(
            audienceTargetingsEditors,
            groupValue.audienceTargetings
        );

        let result: TextPredictor[] = [];

        forEach(audienceTargetings, (value, key) => {
            if (key === AudienceTargetingsType.interests) {
                result = result.concat(getPredictorInterestsText(interestId, audienceTargetings[key]));
            } else if (key === AudienceTargetingsType.socialDemo) {
                result = result.concat(
                    getPredictorPreviewText(cryptaSegmentsByType[WebGoalType.social_demo], audienceTargetings[key])
                );
            } else if (key === AudienceTargetingsType.audioGenres) {
                result = result.concat(
                    getPredictorPreviewText(cryptaSegmentsByType[WebGoalType.audio_genres], audienceTargetings[key])
                );
            } else {
                result = result.concat(getPredictorPreviewText(cryptaSegmentsByType[key], audienceTargetings[key]));
            }
        });

        return result;
    }
)((state, groupId) => groupId);

export const getAudienceTargetingsText = createReReSelector(
    (state: State, groupId: string) => selectEditGroupValueInfo(state, groupId),
    (state: State) => getInterestsIds(state),
    (state: State) => getAllSegmentsGroupByType(state),
    (
        editGroupDataValue: EditCpmGroupDataValue,
        interestId: InterestsIdsType,
        cryptaSegmentsByType: CryptaSegmentsByType
    ): Record<string, SegmentCategoryText[]> => {
        const audienceTargetings = editGroupDataValue.audienceTargetings;

        return sortBy(Object.keys(audienceTargetings), (condition: AudienceTargetingsType): number =>
            audienceTargetingsTypeOrderedList.indexOf(condition)
        ).reduce((result, key): Record<string, SegmentCategoryText[]> => {
            const audienceTargetingsValue = audienceTargetings[key];

            if (
                !audienceTargetingsValue ||
                key === AudienceTargetingsType.audioGenres ||
                (key !== AudienceTargetingsType.audienceMetrics && !audienceTargetingsValue.length)
            ) {
                return result;
            }

            switch (key) {
                case AudienceTargetingsType.interests:
                    transformInterestsToSegments(audienceTargetingsValue, interestId, true).forEach(
                        (interest, index) => {
                            return (result[`interest${index}`] = [interest]);
                        }
                    );
                    return result;
                case AudienceTargetingsType.socialDemo:
                    result[key] = getPreviewSegment(
                        cryptaSegmentsByType[WebGoalType.social_demo],
                        audienceTargetingsValue
                    );
                    return result;
                case AudienceTargetingsType.audienceMetrics:
                    result[key] = getPreviewMetrikaSegment(audienceTargetingsValue);
                    return result;
                default:
                    result[key] = getPreviewSegment(cryptaSegmentsByType[key], audienceTargetingsValue);
                    return result;
            }
        }, {});
    }
)((state, groupId) => groupId);

export const getAudienceMetricsText = createReReSelector(
    (state: State, groupId: string) => getAudienceMetricsValue(state, groupId),
    (audienceMetrics?: RetargetingConditionData): TextPredictor[] => {
        return getPredictorMetricsText(audienceMetrics);
    }
)((state, groupId) => groupId);

export const getAudienceMetricsValue = createReReSelector(
    (state: State, groupId: string) => selectEditGroupValueInfo(state, groupId),
    (state: State, groupId: string) => state.retargetingAudienceEditor[groupId],
    (
        groupValue: EditCpmGroupDataValue,
        data: RetargetingConditionAudienceEditorState
    ): RetargetingConditionData | undefined => {
        if (data) {
            if (data.isDeleting) {
                return;
            }

            return data;
        }

        return groupValue.audienceTargetings[AudienceTargetingsType.audienceMetrics];
    }
)((state, groupId) => groupId);

export const selectAudienceTargetingsEditor = createReReSelector(
    (state: State) => state.audienceTargetingsEditor,
    (state: State, groupId: string) => groupId,
    (editors: AudienceTargetingsEditorsState, groupId: string): AudienceTargetingsEditorState => {
        return editors[groupId] || defaultAudienceTargetingsEditorsState;
    }
)((state, groupId) => groupId);

export const selectAudienceTargetingsEditorDescription = createReReSelector(
    (state: State, groupId: string) => selectEditGroupValueInfo(state, groupId),
    (editGroupDataValue: EditCpmGroupDataValue): AudienceTargetingsEditorDescription => {
        return getAudienceTargetingsEditorDescription(editGroupDataValue.campaignType);
    }
)((state, groupId) => groupId);
